/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */

import { TranslationFn } from '@/hooks/useTranslations';
import { z } from 'zod';

// Regular expression for name validation
// Allows alphabetic characters, accents, hyphens, spaces (within names but not at beginning/end) and apostrophes
// Prevents consecutive hyphens, apostrophes and spaces
const nameRegex = /^[A-Za-zÀ-ÖØ-öø-ÿ](?:[A-Za-zÀ-ÖØ-öø-ÿ]|(?:[-' ][A-Za-zÀ-ÖØ-öø-ÿ]))*$/;
const userNameRegex = /^[A-Za-z0-9._]+$/;
/**
 * Capitalizes the first letter of each word in a name, including after spaces,
 * hyphens, and apostrophes while preserving the casing of the rest of the word.
 *
 * @param name A valid name string
 * @returns The name with first letter of each word capitalized
 */
function capitalizeName(name: string): string {
    // Trim any possible whitespace first
    const trimmed = name.trim();

    // Handle empty string case
    if (trimmed.length === 0) {
        return '';
    }

    // Look for word boundaries: beginning of string, or after space, hyphen, or apostrophe
    return trimmed.replace(/(^|[-' ])[a-zà-öø-ÿ]/g, match => {
        // If the match starts with a delimiter, preserve it and capitalize the next character
        if (match.length > 1) {
            return match[0] + match[1].toUpperCase();
        }
        // Otherwise just capitalize the character (beginning of string case)
        return match.toUpperCase();
    });
}

// Zod schema for name fields
const getNameSchema = (fieldName: string, t: TranslationFn) =>
    z
        .string()
        .trim()
        .min(2, { message: t('NAME_LENGTH', fieldName) })
        .max(50, { message: t('NAME_LENGTH', fieldName) })
        .regex(nameRegex, {
            message: t('NAME_CONTAINS_INVALID_CHAR', fieldName),
        })
        .transform(capitalizeName);

export { getNameSchema, nameRegex, userNameRegex, capitalizeName };
